import type {
  Movement,
  MovementCategory,
} from "@vlab-corporation/vlab-pilates-service-interface/movement";
import type { RegularClass } from "@vlab-corporation/vlab-pilates-service-interface/class";
import type { OTClass } from "@vlab-corporation/vlab-pilates-service-interface/class";
import type { BranchPolicy } from "@vlab-corporation/vlab-pilates-service-interface/branch";
import type { JournalTemplate } from "@vlab-corporation/vlab-pilates-service-interface/class";
import type { Member } from "@vlab-corporation/vlab-pilates-service-interface/customer";
import type { AuthUser } from "@/types/auth";
import type { JournalDocument } from "@/services/firestore/journals";
import type { ClassCompletionDocument } from "@/services/firestore/classCompletions";
import type { SettlementDocument } from "@/services/firestore/settlements";

// ── Helpers ──

function toDateString(date: Date): string {
  return date.toISOString().split("T")[0];
}

function toYearMonth(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

const now = new Date();
const yesterday = new Date(now);
yesterday.setDate(now.getDate() - 1);
const tomorrow = new Date(now);
tomorrow.setDate(now.getDate() + 1);

const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

export const TODAY = toDateString(now);
export const YESTERDAY = toDateString(yesterday);
export const TOMORROW = toDateString(tomorrow);
export const THIS_MONTH = toYearMonth(now);
export const LAST_MONTH = toYearMonth(lastMonth);

// ── Demo User ──

export const DEMO_INSTRUCTOR_ID = "demo-instructor-001";
export const DEMO_CENTER_ID = 1;
export const DEMO_CENTER_NAME = "데모 스튜디오";
export const DEMO_OWNER_UID = "demo-owner-uid";

export const DEMO_USER: AuthUser = {
  uid: "demo-user-uid",
  name: "홍길동 강사",
  role: "instructor",
  memberships: [
    {
      centerOwnerUid: DEMO_OWNER_UID,
      centerId: DEMO_CENTER_ID,
      centerName: DEMO_CENTER_NAME,
      instructorId: DEMO_INSTRUCTOR_ID,
    },
  ],
  activeMembership: {
    centerOwnerUid: DEMO_OWNER_UID,
    centerId: DEMO_CENTER_ID,
    centerName: DEMO_CENTER_NAME,
    instructorId: DEMO_INSTRUCTOR_ID,
  },
};

// ── Movements ──

function mov(id: string, name: string, category: MovementCategory, description?: string): Movement {
  return { id, name, category, ...(description ? { description } : {}) };
}

export const DEMO_MOVEMENTS: Movement[] = [
  // Mat
  mov("mov-mat-001", "Hundred", "mat", "복부 강화와 호흡 조절을 위한 기본 동작"),
  mov("mov-mat-002", "Roll Up", "mat", "척추 분절 움직임과 복부 제어"),
  mov("mov-mat-003", "Single Leg Circle", "mat", "고관절 안정성과 유연성 향상"),
  mov("mov-mat-004", "Rolling Like a Ball", "mat", "밸런스와 척추 마사지"),
  mov("mov-mat-005", "Single Leg Stretch", "mat", "코어 안정성과 다리 근력"),
  // Reformer
  mov("mov-ref-001", "Footwork", "reformer", "하체 정렬과 근력 강화"),
  mov("mov-ref-002", "Elephant", "reformer", "햄스트링 스트레칭과 코어 제어"),
  mov("mov-ref-003", "Long Stretch", "reformer", "전신 안정성과 팔 근력"),
  mov("mov-ref-004", "Stomach Massage", "reformer", "복부 강화와 척추 유연성"),
  mov("mov-ref-005", "Short Box", "reformer", "척추 유연성과 코어 강화"),
  // Cadillac
  mov("mov-cad-001", "Roll Down", "cadillac", "척추 분절과 복부 제어"),
  mov("mov-cad-002", "Push Through", "cadillac", "전신 스트레칭"),
  mov("mov-cad-003", "Leg Spring", "cadillac", "하체 근력과 안정성"),
  mov("mov-cad-004", "Arm Spring", "cadillac", "상체 근력과 어깨 안정성"),
  mov("mov-cad-005", "Hanging", "cadillac", "전신 근력과 유연성"),
  // Chair
  mov("mov-chr-001", "Pumping", "chair", "하체 근력 강화"),
  mov("mov-chr-002", "Side Pull Up", "chair", "측면 근력과 밸런스"),
  mov("mov-chr-003", "Going Up Front", "chair", "코어 및 하체 통합 운동"),
  mov("mov-chr-004", "Swan", "chair", "후방 체인 강화"),
  mov("mov-chr-005", "Teaser", "chair", "코어 제어와 밸런스"),
  // Barrel
  mov("mov-bar-001", "Side Bend", "barrel", "측면 유연성과 근력"),
  mov("mov-bar-002", "Short Box Series", "barrel", "척추 유연성과 코어"),
  mov("mov-bar-003", "Horse Back", "barrel", "후방 체인 스트레칭"),
  mov("mov-bar-004", "Backward Stretch", "barrel", "전면 체인 스트레칭"),
  mov("mov-bar-005", "Breathing", "barrel", "호흡 확장과 흉추 유연성"),
  // Other
  mov("mov-oth-001", "Standing Roll Down", "other", "전신 스트레칭과 호흡"),
  mov("mov-oth-002", "Wall Series", "other", "자세 교정과 정렬"),
  mov("mov-oth-003", "Breathing Exercise", "other", "호흡 패턴 개선"),
  mov("mov-oth-004", "Balance Work", "other", "균형감각과 고유수용감각"),
  mov("mov-oth-005", "Cool Down", "other", "정리 운동과 이완"),
];

// ── Regular Classes ──

export const DEMO_REGULAR_CLASSES: RegularClass[] = [
  // Today: 3 regular (2 completed, 1 scheduled)
  {
    id: "class-reg-today-001",
    memberId: "member-001",
    instructorId: DEMO_INSTRUCTOR_ID,
    membershipId: "membership-001",
    status: "completed",
    date: TODAY,
  },
  {
    id: "class-reg-today-002",
    memberId: "member-002",
    instructorId: DEMO_INSTRUCTOR_ID,
    membershipId: "membership-002",
    status: "completed",
    date: TODAY,
  },
  {
    id: "class-reg-today-003",
    memberId: "member-003",
    instructorId: DEMO_INSTRUCTOR_ID,
    membershipId: "membership-003",
    status: "scheduled",
    date: TODAY,
  },
  // Yesterday: 2 regular (all completed)
  {
    id: "class-reg-yest-001",
    memberId: "member-001",
    instructorId: DEMO_INSTRUCTOR_ID,
    membershipId: "membership-001",
    status: "completed",
    date: YESTERDAY,
  },
  {
    id: "class-reg-yest-002",
    memberId: "member-004",
    instructorId: DEMO_INSTRUCTOR_ID,
    membershipId: "membership-004",
    status: "completed",
    date: YESTERDAY,
  },
  // Tomorrow: 2 regular (scheduled)
  {
    id: "class-reg-tmrw-001",
    memberId: "member-002",
    instructorId: DEMO_INSTRUCTOR_ID,
    membershipId: "membership-002",
    status: "scheduled",
    date: TOMORROW,
  },
  {
    id: "class-reg-tmrw-002",
    memberId: "member-005",
    instructorId: DEMO_INSTRUCTOR_ID,
    membershipId: "membership-005",
    status: "scheduled",
    date: TOMORROW,
  },
];

// ── OT Classes ──

export const DEMO_OT_CLASSES: OTClass[] = [
  // Today: 2 OT (1 completed, 1 reserved)
  {
    id: "class-ot-today-001",
    leadId: "lead-001",
    instructorId: DEMO_INSTRUCTOR_ID,
    status: "completed",
    date: TODAY,
  },
  {
    id: "class-ot-today-002",
    leadId: "lead-002",
    instructorId: DEMO_INSTRUCTOR_ID,
    status: "reserved",
    date: TODAY,
  },
  // Yesterday: 2 OT (1 completed, 1 cancelled)
  {
    id: "class-ot-yest-001",
    leadId: "lead-001",
    instructorId: DEMO_INSTRUCTOR_ID,
    status: "completed",
    date: YESTERDAY,
  },
  {
    id: "class-ot-yest-002",
    leadId: "lead-003",
    instructorId: DEMO_INSTRUCTOR_ID,
    status: "cancelled",
    date: YESTERDAY,
  },
  // Tomorrow: 1 OT (reserved)
  {
    id: "class-ot-tmrw-001",
    leadId: "lead-002",
    instructorId: DEMO_INSTRUCTOR_ID,
    status: "reserved",
    date: TOMORROW,
  },
];

// ── Members ──

export const DEMO_MEMBERS: Member[] = [
  { id: "member-001", name: "김지수", memberType: "reRegistration" },
  { id: "member-002", name: "박민준", memberType: "firstPurchase" },
  { id: "member-003", name: "이서연", memberType: "firstPurchase" },
  { id: "member-004", name: "최재원", memberType: "reRegistration" },
  { id: "member-005", name: "정다은", memberType: "reRegistration" },
];

// ── Class Completions ──

export const DEMO_CLASS_COMPLETIONS: ClassCompletionDocument[] = [
  // Today completed classes with journals
  {
    classId: "class-reg-today-001",
    journalWritten: true,
    journalId: "journal-001",
    updatedAt: null,
  },
  {
    classId: "class-reg-today-002",
    journalWritten: true,
    journalId: "journal-002",
    updatedAt: null,
  },
  {
    classId: "class-ot-today-001",
    journalWritten: true,
    journalId: "journal-003",
    updatedAt: null,
  },
  // Yesterday completed
  {
    classId: "class-reg-yest-001",
    journalWritten: true,
    journalId: "journal-004",
    updatedAt: null,
  },
  {
    classId: "class-reg-yest-002",
    journalWritten: true,
    journalId: "journal-005",
    updatedAt: null,
  },
  { classId: "class-ot-yest-001", journalWritten: true, journalId: "journal-006", updatedAt: null },
];

// ── Journals ──

export const DEMO_JOURNALS: (JournalDocument & { id: string })[] = [
  {
    id: "journal-001",
    classId: "class-reg-today-001",
    instructorId: DEMO_INSTRUCTOR_ID,
    classType: "PRIVATE_REGULAR",
    isOt: false,
    equipments: ["reformer"],
    exercises: [
      { name: "Footwork (heels)", movementId: "mov-ref-001" },
      { name: "Footwork (arches)", movementId: "mov-ref-002" },
      { name: "Lunge", movementId: "mov-ref-003" },
    ],
    problemList: "고관절 안정화를 통한 하체 정렬 개선",
    notes: "오늘 컨디션 좋음. Footwork 자세 개선됨.",
    createdAt: null,
    updatedAt: null,
  },
  {
    id: "journal-002",
    classId: "class-reg-today-002",
    instructorId: DEMO_INSTRUCTOR_ID,
    classType: "PRIVATE_REGULAR",
    isOt: false,
    equipments: ["mat"],
    exercises: [
      { name: "Hundred", movementId: "mov-mat-001" },
      { name: "Roll Up", movementId: "mov-mat-002" },
      { name: "Leg Circles", movementId: "mov-mat-003", notes: "왼쪽 고관절 주의" },
      { name: "Rolling Like a Ball", movementId: "mov-mat-004" },
    ],
    problemList: "코어 안정화 및 척추 이동성 향상",
    notes: "매트 기본 동작 복습. 다음 시간 체어 도입 예정.",
    createdAt: null,
    updatedAt: null,
  },
  {
    id: "journal-003",
    classId: "class-ot-today-001",
    instructorId: DEMO_INSTRUCTOR_ID,
    classType: "PRIVATE_REGULAR",
    isOt: true,
    equipments: ["reformer", "mat"],
    exercises: [
      { name: "Footwork (heels)", movementId: "mov-ref-001" },
      { name: "Hundred", movementId: "mov-mat-001" },
      { name: "체형 분석 동작", movementId: "mov-oth-001" },
    ],
    problemList: "OT 평가: 전반적 체형 분석 및 목표 설정",
    notes: "체험 수업. 리포머 기초 설명 중심.",
    bodyAnalysis: "전만 경향, 흉추 경직",
    memberGoal: "자세 교정 및 코어 강화",
    createdAt: null,
    updatedAt: null,
  },
  {
    id: "journal-004",
    classId: "class-reg-yest-001",
    instructorId: DEMO_INSTRUCTOR_ID,
    classType: "PRIVATE_REGULAR",
    isOt: false,
    equipments: ["cadillac"],
    exercises: [
      { name: "Roll Down Bar", movementId: "mov-cad-001" },
      { name: "Push Thru (sitting)", movementId: "mov-cad-002" },
      { name: "Swan Series", movementId: "mov-cad-003" },
    ],
    problemList: "척추 신전 및 어깨 안정화",
    createdAt: null,
    updatedAt: null,
  },
  {
    id: "journal-005",
    classId: "class-reg-yest-002",
    instructorId: DEMO_INSTRUCTOR_ID,
    classType: "PRIVATE_REGULAR",
    isOt: false,
    equipments: ["chair"],
    exercises: [
      { name: "Standing Roll Down", movementId: "mov-chr-001" },
      { name: "Flat Back Roll Down", movementId: "mov-chr-002" },
      { name: "One Leg Teaser", movementId: "mov-chr-004" },
    ],
    problemList: "균형 감각 및 코어 연결성 강화",
    notes: "체어 프로그램 진행. Swan 자세 교정 필요.",
    createdAt: null,
    updatedAt: null,
  },
  {
    id: "journal-006",
    classId: "class-ot-yest-001",
    instructorId: DEMO_INSTRUCTOR_ID,
    classType: "PRIVATE_REGULAR",
    isOt: true,
    equipments: ["reformer"],
    exercises: [
      { name: "Footwork (heels)", movementId: "mov-ref-001" },
      { name: "Stomach Massage", movementId: "mov-ref-004" },
    ],
    problemList: "OT 평가: 리포머 기초 동작 숙지",
    createdAt: null,
    updatedAt: null,
  },
];

// ── Settlements ──

export const DEMO_SETTLEMENTS: SettlementDocument[] = [
  {
    yearMonth: THIS_MONTH,
    status: "confirmed",
    instructorSettlements: [
      {
        instructorId: DEMO_INSTRUCTOR_ID,
        completedClasses: 8,
        totalAmount: 380000,
        lineItems: [
          {
            classId: "class-reg-today-001",
            memberType: "reRegistration",
            unitPrice: 40000,
            amount: 40000,
          },
          {
            classId: "class-reg-today-002",
            memberType: "firstPurchase",
            unitPrice: 50000,
            amount: 50000,
          },
          {
            classId: "class-ot-today-001",
            memberType: "firstPurchase",
            unitPrice: 50000,
            amount: 50000,
          },
          {
            classId: "class-reg-yest-001",
            memberType: "reRegistration",
            unitPrice: 40000,
            amount: 40000,
          },
          {
            classId: "class-reg-yest-002",
            memberType: "reRegistration",
            unitPrice: 40000,
            amount: 40000,
          },
          {
            classId: "class-ot-yest-001",
            memberType: "firstPurchase",
            unitPrice: 50000,
            amount: 50000,
          },
          {
            classId: "class-past-001",
            memberType: "reRegistration",
            unitPrice: 40000,
            amount: 40000,
          },
          {
            classId: "class-past-002",
            memberType: "reRegistration",
            unitPrice: 40000,
            amount: 40000,
          },
        ],
      },
    ],
    calculatedAt: null,
  },
  {
    yearMonth: LAST_MONTH,
    status: "paid",
    instructorSettlements: [
      {
        instructorId: DEMO_INSTRUCTOR_ID,
        completedClasses: 20,
        totalAmount: 880000,
        lineItems: Array.from({ length: 20 }, (_, i) => ({
          classId: `class-last-${String(i + 1).padStart(3, "0")}`,
          memberType: (i % 3 === 0 ? "firstPurchase" : "reRegistration") as
            | "firstPurchase"
            | "reRegistration",
          unitPrice: i % 3 === 0 ? 50000 : 40000,
          amount: i % 3 === 0 ? 50000 : 40000,
        })),
      },
    ],
    calculatedAt: null,
  },
];

// ── Branch Policy ──

export const DEMO_BRANCH_POLICY: BranchPolicy = {
  membershipPricing: [],
  instructorPayScale: [
    { memberType: "firstPurchase", unitPrice: 50000 },
    { memberType: "reRegistration", unitPrice: 40000 },
  ],
  journalFormat: { enabled: true },
};

// ── Journal Templates ──

export const DEMO_JOURNAL_TEMPLATES: JournalTemplate[] = [
  {
    id: "template-001",
    name: "기본 리포머 템플릿",
    defaultMovementIds: ["mov-ref-001", "mov-ref-002", "mov-ref-005"],
  },
];

// ── Studiomate Mock Data ──

export const DEMO_STAFFS = [
  {
    id: 1,
    studio_id: 1,
    user_grade_id: null,
    name: "홍길동",
    account_type: "staffAccount" as const,
    account_id: 1,
    mobile: "010-1234-5678",
    vaccination_yn: "Y" as const,
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    registered_at: "2024-01-01T00:00:00Z",
    deleted_at: null,
    lectures_count: 120,
    counsels_count: 15,
    profile: {
      id: 1,
      studio_id: 1,
      studio_user_id: 1,
      name: "홍길동",
      gender: "M" as const,
      work_type_id: null,
      birthday: "1990-05-15",
      web_token: null,
      self_introduction: "필라테스 강사 5년차",
      representative_color: "#4F46E5",
      hired_at: "2020-03-01",
      get_noti_all: 1 as const,
      private_start_interval_on_off: 0 as const,
      private_start_interval_am: 0,
      private_start_interval_pm: 0,
      registered_at: "2024-01-01T00:00:00Z",
      created_at: "2024-01-01T00:00:00Z",
      updated_at: "2024-01-01T00:00:00Z",
      deleted_at: null,
      work_type: null,
    },
    contact_infos: [],
    avatars: [],
    roles: [],
  },
];

export const DEMO_PRODUCTS = [
  {
    id: 1,
    studio_id: 1,
    goods_type: "ticket" as const,
    actual_price: 300000,
    original_price: 350000,
    reward_point: 0,
    is_selling: true,
    is_selling_term_infinite: true,
    selling_start_at: null,
    selling_end_at: null,
    is_promotion: false,
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    deleted_at: null,
    ticket_count: 10,
  },
  {
    id: 2,
    studio_id: 1,
    goods_type: "ticket" as const,
    actual_price: 550000,
    original_price: 600000,
    reward_point: 5000,
    is_selling: true,
    is_selling_term_infinite: true,
    selling_start_at: null,
    selling_end_at: null,
    is_promotion: false,
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    deleted_at: null,
    ticket_count: 20,
  },
];
